/**
 * Do not disturb (Settings → Notifications → "Pause notifications", the moon on my avatar): my server-side
 * notification settings (logic/dnd) and whether they pause notifications right now, re-checked exactly when that can
 * change: a pause running out, or a quiet-hours window starting or ending (logic/dnd nextDndChange).
 */
import type { DndSchedule, NotificationSettings } from "@bundle/contracts";
import { type PauseOption, isPaused, nextDndChange, pauseEndsAt, pauseUntil, resume } from "../logic/dnd.ts";
import type { Clock, SettingsApi } from "../ports.ts";
import { ViewModel } from "./ViewModel.ts";

export type DndState = {
  status: "loading" | "ready" | "failed";
  settings: NotificationSettings;
  /** Notifications are paused right now. */
  paused: boolean;
  /** A manual pause is running ("Resume notifications" ends it; a schedule window can't be resumed early). */
  manual: boolean;
  /** When the pause ends (ms), while paused. */
  endsAt?: number;
  saving: boolean;
  /** The last save failed (the previous settings are back). */
  saveFailed: boolean;
};

export type DndDeps = {
  api: Pick<SettingsApi, "notificationSettings" | "updateNotificationSettings">;
  clock: Clock;
  /** This device's IANA time zone (new schedules and "until tomorrow" use it). */
  timeZone: () => string;
};

/** Longest single wait before re-checking (timers drift; a far-off pause is re-checked meanwhile). */
const RECHECK_MS = 6 * 60 * 60 * 1000;

export class DndViewModel extends ViewModel<DndState> {
  private cancelTimer: (() => void) | undefined;

  constructor(private readonly deps: DndDeps) {
    super({ status: "loading", settings: {}, paused: false, manual: false, saving: false, saveFailed: false });
    this.onDispose(() => this.cancelTimer?.());
  }

  load(): Promise<void> {
    return this.deps.api.notificationSettings().then(
      (settings) => this.apply(settings, { status: "ready" }),
      () => {
        if (this.state.status === "loading") this.update({ status: "failed" });
      },
    );
  }

  /** "For 30 minutes" … "Until tomorrow". */
  pause(option: Exclude<PauseOption, "custom">): Promise<void> {
    return this.pauseUntil(pauseUntil(option, this.deps.clock.now(), this.deps.timeZone()));
  }

  /** "Custom…": until this time. */
  pauseUntil(until: number): Promise<void> {
    return this.save({ ...this.state.settings, pausedUntil: until });
  }

  /** "Resume notifications" (the schedule stays). */
  resume(): Promise<void> {
    return this.save(resume(this.state.settings));
  }

  /** Sets (or, with undefined, removes) the daily schedule. */
  setSchedule(schedule: DndSchedule | undefined): Promise<void> {
    const next: NotificationSettings = { ...this.state.settings };
    if (schedule === undefined) delete next.schedule;
    else next.schedule = schedule;
    return this.save(next);
  }

  private async save(next: NotificationSettings): Promise<void> {
    const before = this.state.settings;
    this.apply(next, { saving: true, saveFailed: false });
    try {
      this.apply(await this.deps.api.updateNotificationSettings(next), { saving: false });
    } catch {
      this.apply(before, { saving: false, saveFailed: true });
    }
  }

  private apply(settings: NotificationSettings, patch: Partial<DndState>): void {
    const now = this.deps.clock.now();
    const paused = isPaused(now, settings);
    const endsAt = pauseEndsAt(now, settings);
    const manual = settings.pausedUntil !== undefined && settings.pausedUntil > now;
    const next: DndState = { ...this.state, ...patch, settings, paused, manual };
    if (endsAt === undefined) delete next.endsAt;
    else next.endsAt = endsAt;
    this.setState(next);
    this.schedule(now, settings);
  }

  /** Re-checks at the next moment being paused can change (a pause ends, quiet hours start or end). */
  private schedule(now: number, settings: NotificationSettings): void {
    this.cancelTimer?.();
    this.cancelTimer = undefined;
    const next = nextDndChange(now, settings);
    if (next === undefined) return;
    this.cancelTimer = this.deps.clock.setTimer(Math.min(next - now, RECHECK_MS), () => this.apply(this.state.settings, {}));
  }
}
